import { createGlobalStyle, css } from "styled-components";
import { hex2RgbComponents } from "./colorTransforms.js";
import { token, versionSuffix } from "./token.js";
const theme = {
  "--white": "#ffffff",
  "--white-rgb": hex2RgbComponents("#ffffff"),
  // Grays
  "--gray1000": "#031026",
  "--gray1000-rgb": hex2RgbComponents("#031026"),
  "--gray900": "#132034",
  "--gray800": "#212c40",
  "--gray700": "#343e50",
  "--gray600": "#4f5867",
  "--gray500": "#6f7783",
  "--gray400": "#8b919b",
  "--gray300": "#b8bcc2",
  "--gray200": "#d5d7da",
  "--gray100": "#e6e7e9",
  "--gray75": "#edeef0",
  "--gray50": "#f2f3f4",
  "--gray25": "#f8f8f9",
  "--gray10": "#ffffff",
  // System colors
  "--blue1000": "#031025",
  "--blue900": "#051e46",
  "--blue800": "#072961",
  "--blue700": "#093986",
  "--blue600": "#0d51bc",
  "--blue500": "#126dfe",
  "--blue500-rgb": hex2RgbComponents("#126dfe"),
  "--blue500a10": `rgb(${token["blue500-rgb"]} / 10%)`,
  "--blue400": "#488fff",
  "--blue300": "#94bdff",
  "--blue200": "#c0d8ff",
  "--blue100": "#dae8ff",
  "--blue75": "#e5efff",
  "--blue50": "#ecf3ff",
  "--blue25": "#f4f8ff",
  "--red1000": "#1f0908",
  "--red900": "#3b120f",
  "--red800": "#521914",
  "--red700": "#71221c",
  "--red600": "#9e3028",
  "--red500": "#d54035",
  "--red500-rgb": hex2RgbComponents("#d54035"),
  "--red500a50": `rgb(${token["red500-rgb"]} / 50%)`,
  "--red500a10": `rgb(${token["red500-rgb"]} / 10%)`,
  "--red400": "#e16e66",
  "--red300": "#eeaba6",
  "--red200": "#f5cecb",
  "--red100": "#f9e2e1",
  "--red75": "#fbebea",
  "--red50": "#fcf0ef",
  "--red25": "#fdf7f6",
  "--yellow500": "#fea900",
  "--yellow500-rgb": hex2RgbComponents("#fea900"),
  "--yellow500a10": `rgb(${token["yellow500-rgb"]} / 10%)`,
  "--yellow600": "#e59800",
  "--yellow700": "#cb8700",
  "--green1000": "#001405",
  "--green900": "#002609",
  "--green800": "#00340c",
  "--green700": "#004911",
  "--green600": "#006618",
  "--green500": "#038922",
  "--green400": "#3ca454",
  "--green300": "#8cc99b",
  "--green200": "#bbdfc4",
  "--green100": "#d7eddc",
  "--green75": "#e3f2e7",
  "--green50": "#ebf5ed",
  "--green25": "#f4faf5",
  "--purple1000": "#100e20",
  "--purple900": "#1e1b3e",
  "--purple800": "#2a2656",
  "--purple700": "#3b3478",
  "--purple600": "#534aa9",
  "--purple500": "#7064e4",
  "--purple400": "#8d84ea",
  "--purple300": "#bab5f3",
  "--purple200": "#d6d3f8",
  "--purple100": "#e7e5fb",
  "--purple75": "#eeedfc",
  "--purple50": "#f3f2fd",
  "--purple25": "#f8f7fe",
  "--magenta1000": "#1f0910",
  "--magenta900": "#3b101e",
  "--magenta800": "#511729",
  "--magenta700": "#701f39",
  "--magenta600": "#9d2c50",
  "--magenta500": "#d33b6c",
  "--magenta400": "#e2688f",
  "--magenta300": "#eea8be",
  "--magenta200": "#f5ccd9",
  "--magenta100": "#f9e1e9",
  "--magenta75": "#fbeaf0",
  "--magenta50": "#fcf0f4",
  "--magenta25": "#fdf6f9",
  "--teal1000": "#00140e",
  "--teal900": "#00251b",
  "--teal800": "#003325",
  "--teal700": "#004734",
  "--teal600": "#006449",
  "--teal500": "#008762",
  "--teal500-rgb": hex2RgbComponents("#008762"),
  "--teal500a10": `rgb(${token["teal500-rgb"]} / 10%)`,
  "--teal400": "#35a284",
  "--teal300": "#88c8b7",
  "--teal200": "#b9dfd4",
  "--teal100": "#d6ece6",
  "--teal75": "#e2f2ee",
  "--teal50": "#eaf5f2",
  "--teal25": "#f3faf8",
  // Brand colors
  "--brandPrimary700": themableToken("brandPrimary700", "#093986"),
  "--brandPrimary600": themableToken("brandPrimary600", "#0d51bc"),
  "--brandPrimary500": themableToken("brandPrimary500", "#126dfe"),
  "--brandPrimary500-rgb": themableToken("brandPrimary500-rgb", hex2RgbComponents("#126dfe")),
  "--brandPrimary300": themableToken("brandPrimary300", "#94bdff"),
  "--brandPrimary100": themableToken("brandPrimary100", "#dae8ff"),
  // Spacing
  "--spacing1": "2px",
  "--spacing2": "4px",
  "--spacing3": "8px",
  "--spacing4": "12px",
  "--spacing5": "16px",
  "--spacing6": "24px",
  "--spacing7": "32px",
  "--spacing8": "40px",
  "--spacing9": "48px",
  // Layout
  "--layoutSpacing1": "16px",
  "--layoutSpacing2": "24px",
  "--layoutSpacing3": "32px",
  "--layoutSpacing4": "48px",
  "--layoutSpacing5": "64px",
  "--layoutSpacing6": "96px",
  "--layoutSpacing7": "160px",
  // Border radius
  "--radiusSmall": "2px",
  "--radiusMedium": "5px",
  "--radiusLarge": "8px",
  "--radiusCircle": "50%",
  "--radiusPill": "9999px",
  "--radiusBase": token.radiusMedium,
  "--brandRadiusBase": token.radiusBase,
  // Fonts
  "--fontStackBase": "Inter, system-ui, sans-serif",
  "--brandFontStackBase": themableToken("brandFontStackBase", "inherit"),
  "--fontSize25": "10px",
  "--fontSize50": "12px",
  "--fontSize75": "14px",
  "--fontSize100": "16px",
  "--fontSize200": "18px",
  "--fontSize300": "20px",
  "--fontSize400": "22px",
  "--fontSize500": "25px",
  "--fontSize600": "28px",
  "--fontSize700": "32px",
  "--fontSize800": "36px",
  "--fontSize900": "40px",
  "--fontSize1000": "45px",
  "--fontSizeBase": token.fontSize100,
  "--lineHeightBase": "1.25",
  "--lineHeightHeading": "1.25",
  "--lineHeightText": "1.5",
  // Alpha grays
  "--gray900a": `rgb(${token["gray1000-rgb"]} / 93%)`,
  "--gray800a": `rgb(${token["gray1000-rgb"]} / 88%)`,
  "--gray700a": `rgb(${token["gray1000-rgb"]} / 81%)`,
  "--gray600a": `rgb(${token["gray1000-rgb"]} / 70%)`,
  "--gray500a": `rgb(${token["gray1000-rgb"]} / 57%)`,
  "--gray400a": `rgb(${token["gray1000-rgb"]} / 46%)`,
  "--gray300a": `rgb(${token["gray1000-rgb"]} / 28%)`,
  "--gray200a": `rgb(${token["gray1000-rgb"]} / 17%)`,
  "--gray100a": `rgb(${token["gray1000-rgb"]} / 10%)`,
  "--gray75a": `rgb(${token["gray1000-rgb"]} / 7%)`,
  "--gray50a": `rgb(${token["gray1000-rgb"]} / 5%)`,
  "--gray25a": `rgb(${token["gray1000-rgb"]} / 3%)`,
  // Aliases
  "--colorText": token.gray800,
  "--colorTextMuted": token.gray600,
  "--colorTextDisabled": token.gray400,
  // Focus rings
  "--focusRingWidth": "2px",
  "--focusRingColorPrimary": token.blue500,
  "--focusRingColorSecondary": token.gray200,
  // Shadows
  "--colorShadow": token.gray100a,
  "--elevationStatic": `0 0 0 1px ${token.colorShadow}`,
  "--elevation1": `0 1px 4px -1px ${token.colorShadow}`,
  "--elevation2": `0 2px 4px -1px ${token.colorShadow}`,
  "--elevation4": `0 4px 6px -2px ${token.colorShadow}`,
  "--elevation8": `0 8px 12px -2px ${token.colorShadow}`,
  "--elevation12": `0 12px 12px -2px ${token.colorShadow}`,
  "--elevation16": `0 16px 12px -2px ${token.colorShadow}`,
  "--elevation24": `0 24px 12px -2px ${token.colorShadow}`
};
const darkTheme = {
  "--gray1000": "#ffffff",
  "--gray1000-rgb": hex2RgbComponents("#ffffff"),
  "--gray900": "#e6e7e9",
  "--gray800": "#cdcfd4",
  "--gray700": "#b3b7be",
  "--gray600": "#9a9fa8",
  "--gray500": "#818793",
  "--gray400": "#68707d",
  "--gray300": "#4f5867",
  "--gray200": "#354051",
  "--gray100": "#1c283c",
  "--gray75": "#152135",
  "--gray50": "#101c31",
  "--gray25": "#0b172c",
  "--gray10": "#031026",
  "--focusRingColorSecondary": token.gray200a
};
const lightThemeClass = "ds-light-theme";
const darkThemeClass = "ds-dark-theme";
const ThemeStyles = createGlobalStyle`${css([":root,:host,.", ",.", "{", "}.", "{", "}"], lightThemeClass, darkThemeClass, stringifyTheme(theme), darkThemeClass, stringifyTheme(darkTheme))}`;
function stringifyTheme(themeObject) {
  const propertiesAndValues = Object.entries(themeObject).map(([name, value]) => {
    return `${name}-${versionSuffix}: ${value}`;
  });
  return propertiesAndValues.join(";") + ";";
}
function themableToken(tokenName, defaultValue) {
  return `var(--user-${tokenName}, var(--theme-${tokenName}, ${defaultValue}))`;
}
export {
  ThemeStyles,
  darkTheme,
  darkThemeClass,
  lightThemeClass,
  theme
};
//# sourceMappingURL=theme.js.map
